import {
  ISeasonMapping,
  ITouPeriods,
  IWorkDay,
  TypeHour,
  TypeMonth,
  TypeSeason,
  TypeWorkDay,
  ILoadTimeSum,
  TypeLoadTime,
  ITimeDataItem,
  IMonthNum,
} from './algoInterface'
import { deepCopyTimeDataArray } from './economicAlgo'

/**
 * 발전량, 소비량 데이터 날짜를 보고 계절을 판단
 * @param {ITimeDataItem[]} data - 데이터
 * @param {ISeasonMapping} seasonMapping - 월별 계절
 * @returns {ITimeDataItem[]} season 항목 추가된 데이터
 */
export const classifySeason = (
  timeData: ITimeDataItem[],
  seasonMapping: ISeasonMapping
): ITimeDataItem[] => {
  const data = timeData.map((item) => ({ ...item }))
  for (const item of data) {
    const month = (new Date(item.date).getMonth() + 1) as TypeMonth
    item.season = seasonMapping[month] as TypeSeason
  }
  return data
}

/**
 * 발전량, 소비량 데이터 날짜를 보고 공휴일 유무 판단
 * @param {ITimeDataItem[]} data - 데이터
 * @param {IholidayData} holidayData - 공휴일 데이터
 * @returns {ITimeDataItem[]} workDay 항목 추가된 데이터
 */
export const classifyWeekDay = (
  timeData: ITimeDataItem[],
  holidayData: number[]
): ITimeDataItem[] => {
  const data: ITimeDataItem[] = JSON.parse(JSON.stringify(timeData))

  data.forEach((item) => {
    // 년월일만 추출해 정수로 변환
    const currentDate = parseInt(
      item.date.substring(0, 10).replace(/-/g, ''),
      10
    )

    // 공휴일 데이터에 날짜가 있으면
    if (holidayData.includes(currentDate)) {
      // workDay 칼럼 holi
      item.workDay = 'holi' as TypeWorkDay
    } else {
      const dayOfWeek = new Date(item.date).getDay()

      if (dayOfWeek === 0) {
        // 일
        item.workDay = 'holi' as TypeWorkDay
      } else if (dayOfWeek === 6) {
        // 토
        item.workDay = 'sat' as TypeWorkDay
      } else {
        // 나머지 평일
        item.workDay = 'week' as TypeWorkDay
      }
    }
  })
  return data
}

/**
 * 발전량, 소비량 데이터 날짜에서 시간대 추출
 * @param {ITimeDataItem[]} data - 데이터
 * @returns {ITimeDataItem[]} hour,month 항목 추가된 데이터
 */
export const classifyHourMonth = (
  timeData: ITimeDataItem[]
): ITimeDataItem[] => {
  const data: ITimeDataItem[] = JSON.parse(JSON.stringify(timeData))

  data.forEach((item) => {
    item.hour = new Date(item.date).getHours()
    item.month = (new Date(item.date).getMonth() + 1) as TypeMonth
    item.day = new Date(item.date).getDate()
  })
  return data
}

/**
 * 발전량, 소비량 데이터 + tou 시간대 보고 최저/중간/최대부하 판단
 * @param {ITimeDataItem[]} data - 데이터
 * @param {ITouPeriods} touPeriods - TOU 시간대
 * @returns {ITimeDataItem[]} loadTime 항목 추가된 데이터
 */
const classifyLoadTime = (
  timeData: ITimeDataItem[],
  touPeriods: ITouPeriods
): ITimeDataItem[] => {
  let data: ITimeDataItem[] = JSON.parse(JSON.stringify(timeData))
  // hour 데이터 추가
  data = classifyHourMonth(data)

  //loadTime에 min mid max 추가
  data.forEach((item) => {
    const hour = item.hour as TypeHour
    const season = item.season as TypeSeason
    const workDay = item.workDay as TypeWorkDay

    if (hour !== undefined && season && workDay) {
      item.loadTime = (touPeriods[season] as IWorkDay)[workDay][
        hour
      ] as TypeLoadTime
    }
  })

  return data
}

/**
 * 발전/부하 데이터 로 월별 최저/중간/최대부하 합 계산
 * @param {ITimeDataItem[]} timeData - 발전,부하 데이터
 * @param {ITouPeriods} touPeriods - TOU 시간대
 * @returns {ILoadTimeSum} 월별 최저/중간/최대부하 합
 */
const loadTimeSum = (timeData: ITimeDataItem[], touPeriods: ITouPeriods) => {
  const result: ILoadTimeSum = {
    1: {
      min: 0,
      mid: 0,
      max: 0,
    },
    2: {
      min: 0,
      mid: 0,
      max: 0,
    },
    3: {
      min: 0,
      mid: 0,
      max: 0,
    },
    4: {
      min: 0,
      mid: 0,
      max: 0,
    },
    5: {
      min: 0,
      mid: 0,
      max: 0,
    },
    6: {
      min: 0,
      mid: 0,
      max: 0,
    },
    7: {
      min: 0,
      mid: 0,
      max: 0,
    },
    8: {
      min: 0,
      mid: 0,
      max: 0,
    },
    9: {
      min: 0,
      mid: 0,
      max: 0,
    },
    10: {
      min: 0,
      mid: 0,
      max: 0,
    },
    11: {
      min: 0,
      mid: 0,
      max: 0,
    },
    12: {
      min: 0,
      mid: 0,
      max: 0,
    },
  }

  let data: ITimeDataItem[] = JSON.parse(JSON.stringify(timeData))

  if (!data[0]?.loadTime) {
    //loadTime 추가
    data = classifyLoadTime(data, touPeriods)
  }
  if (!(data[0]?.hour && data[0]?.month)) {
    //hour,month 추가
    data = classifyHourMonth(data)
  }

  for (let index = 1; index < 13; index++) {
    result[index].min = data
      .filter((item) => item.loadTime === 'min' && item.month === index)
      .reduce((sum, item) => sum + item.value, 0)
    result[index].mid = data
      .filter((item) => item.loadTime === 'mid' && item.month === index)
      .reduce((sum, item) => sum + item.value, 0)
    result[index].max = data
      .filter((item) => item.loadTime === 'max' && item.month === index)
      .reduce((sum, item) => sum + item.value, 0)
  }

  return result
}

/**
 * 발전/부하 데이터에서 월별 최저/중간/최대부하 & 발전 합 계산
 * @param {ITimeDataItem[]} data - Time 데이터 (발전/부하)
 * @param {ISeasonMapping} seasonMapping - 월별 계절
 * @param {IholidayData} holidayData - 공휴일
 * @param {ITouPeriods} touPeriods - TOU 시간대
 * @returns {timeDataResult : ITimeDataItem[], loadTimeSumResult : ILoadTimeSum} season, workDay, hour, loadTime 추가된 TimeData
 */
export const calLoadTimeSum = (
  data: ITimeDataItem[],
  seasonMapping: ISeasonMapping,
  holidayData: number[],
  touPeriods: ITouPeriods
): {
  timeDataResult: ITimeDataItem[]
  loadTimeSumResult: ILoadTimeSum
} => {
  let timeData: ITimeDataItem[] = classifySeason(data, seasonMapping)
  timeData = classifyWeekDay(timeData, holidayData)
  timeData = classifyLoadTime(timeData, touPeriods)
  const loadTimeSumResult: ILoadTimeSum = loadTimeSum(timeData, touPeriods)
  return { timeDataResult: timeData, loadTimeSumResult: loadTimeSumResult }
}

/**
 * 발전/부하 데이터에서 월별 발전/부하 합 계산
 * @param {ITimeDataItem[]} data
 * @returns { [month: number]: number } 월별 발전/부하 합
 */
export const calMonthSum = (data: ITimeDataItem[]) => {
  const monthSum: IMonthNum = {
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
    6: 0,
    7: 0,
    8: 0,
    9: 0,
    10: 0,
    11: 0,
    12: 0,
  }
  // 월 데이터 추가
  data = classifyHourMonth(data)
  // 월별로 합하기
  for (let month = 1; month < 13; month++) {
    //@ts-ignore
    monthSum[month] = data
      .filter((v) => v.month === month)
      .reduce((accumulator, currentItem) => {
        return accumulator + (currentItem.value ? currentItem.value : 0)
      }, 0)
  }
  return monthSum
}

/**
 * 시간 데이터 끼리 곱하기 (SMP * 발전량)
 * @param {ITimeDataItem[]} data
 * @param {ITimeDataItem[]} data
 * @returns {ITimeDataItem[]}
 */
export const multiplyValuesByRealTime = (
  data1: ITimeDataItem[],
  data2: ITimeDataItem[]
): ITimeDataItem[] => {
  const result: ITimeDataItem[] = []

  if (!data1[0]?.hour) {
    data1 = classifyHourMonth(data1)
  }
  if (!data2[0]?.hour) {
    data2 = classifyHourMonth(data2)
  }

  for (let index = 0; index < data1.length; index++) {
    const item1 = data1[index]

    const matchingItem = data2.find(
      (item2) =>
        item2.month === item1.month &&
        item2.hour === item1.hour &&
        item2.day === item1.day
    )

    if (matchingItem) {
      result.push({
        hour: item1.hour,
        month: item1.month,
        date: item1.date,
        day: item1.day,
        value: item1.value * matchingItem.value,
      })
    }
  }

  return result
}

//TODO: 아직 테스트 못함
/**
 * 시간 데이터 끼리 곱하기 - SMP가 일단위일때 (SMP * 발전량)
 * @param {ITimeDataItem[]} data
 * @param {ITimeDataItem[]} data
 * @returns {ITimeDataItem[]}
 */
export const multiplyValuesByTime = (
  data1: ITimeDataItem[],
  data2: ITimeDataItem[]
): ITimeDataItem[] => {
  // 그룹화된 데이터를 담을 맵
  const groupedData = new Map<string, number>()

  // 첫 번째 데이터를 반복하여 그룹화
  data1.forEach((item) => {
    const { date, value } = item
    const dateKey = date.split('T')[0] // 연월일 부분을 키로 사용
    if (groupedData.has(dateKey)) {
      groupedData.set(dateKey, groupedData.get(dateKey)! * value)
    } else {
      groupedData.set(dateKey, value)
    }
  })

  // 두 번째 데이터를 반복하여 그룹화
  data2.forEach((item) => {
    const { date, value } = item
    const dateKey = date.split('T')[0] // 연월일 부분을 키로 사용
    if (groupedData.has(dateKey)) {
      groupedData.set(dateKey, groupedData.get(dateKey)! * value)
    } else {
      groupedData.set(dateKey, value)
    }
  })

  // 새로운 배열 생성
  const result: ITimeDataItem[] = []
  groupedData.forEach((value, dateKey) => {
    result.push({
      date: dateKey,
      value: value,
    })
  })

  return result
}

/**
 * TimeData에서 숫자 나누기
 * @param {ITimeDataItem[]} timeData
 * @returns {ITimeDataItem[]}
 */
export const divTimeData = (
  timeData: ITimeDataItem[],
  num: number
): ITimeDataItem[] => {
  const data = deepCopyTimeDataArray(timeData)

  return data.map((item) => ({
    ...item,
    value: (item.value ? item.value : 0) / num,
  }))
}

/**
 * TimeData에서 숫자 곱하기
 * @param {ITimeDataItem[]} timeData
 * @returns {ITimeDataItem[]}
 */
export const mulTimeData = (
  timeData: ITimeDataItem[],
  num: number
): Readonly<ITimeDataItem>[] => {
  const data = timeData.map((item) => ({ ...item, value: item.value * num }))
  return data
}
